import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useScopeStore } from '@/stores/scope'

/**
 * The scope, in the address bar.
 *
 * # Why it is there at all
 *
 * Everything this console draws is a function of the scope, so a link to „Pregled“ that
 * does not carry it is a link to a different screen for whoever opens it. Pasting an
 * address into a message is how an administration works — „poglej si to“ — and the
 * whole point is that both people are then looking at the same houses.
 *
 * # How the two are kept in step
 *
 * The store is the state and the query is its rendering. Each direction writes only
 * when the two actually differ, which is what makes the pair settle rather than chase
 * each other: a `replace` that repeats the value the watcher just read would fire the
 * other watcher, for ever.
 *
 * `replace` and not `push`: the scope selector is a filter and not a place, and forty
 * presses of Back to get out of a morning's comparing is nobody's idea of history.
 *
 * Called once, from the shell.
 */
export const useScopeQuery = () => {
    const route      = useRoute()
    const router     = useRouter()
    const scopeStore = useScopeStore()

    const queryScope = (): string => {
        const value = route.query.scope

        return Array.isArray(value) ? (value[0] ?? '') : (value ?? '')
    }

    /**
     * The address decides on arrival, and the remembered selection decides when the
     * address says nothing.
     *
     * The second half is the reason this is not simply "read the query": somebody who
     * types the bare hostname every morning would otherwise land on the whole group
     * every morning, and a regional director's group is not what they came to see.
     */
    const start = () => {
        const fromUrl = queryScope()

        if (fromUrl) scopeStore.selectFromQuery(fromUrl)
        else if (scopeStore.queryValue) void writeToUrl(scopeStore.queryValue)

        watch(() => scopeStore.queryValue, (value) => {
            if (value !== queryScope()) void writeToUrl(value)
        })

        watch(queryScope, (value) => {
            if (value === scopeStore.queryValue) return

            // An address that carries NO scope is a navigation that did not carry it —
            // the menu links are `{ name }` and nothing else — and not somebody asking
            // for the whole group. So the scope is written back rather than dropped:
            // walking from „Pregled“ to „Struktura“ must not silently widen what
            // somebody is looking at. Clearing it is what the selector's „cela skupina“
            // is for, and that leaves both sides empty and agreeing.
            if (!value) return void writeToUrl(scopeStore.queryValue)

            scopeStore.selectFromQuery(value)
        })
    }

    /** An empty scope leaves the key out entirely: `?scope=` is noise nobody wrote. */
    const writeToUrl = (value: string) => {
        const query = { ...route.query, scope: value || undefined }

        return router.replace({ path: route.path, query }).catch(() => {
            // A navigation cancelled by another one that arrived first. The state is
            // the store's and is already right; only its rendering was dropped.
        })
    }

    return { start }
}
